import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  FileDown, BarChart3, Building2, DollarSign, Users,
  TrendingUp, Activity, Download, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444'];

export default function SuperAdminReports() {
  const [overview, setOverview] = useState(null);
  const [tenantAnalytics, setTenantAnalytics] = useState(null);
  const [timeRange, setTimeRange] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    const token = localStorage.getItem('superAdminToken');
    try {
      const [overviewRes, tenantsRes] = await Promise.all([
        axios.get(`${API}/analytics/platform/overview?time_range=${timeRange}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/analytics/platform/tenants`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setOverview(overviewRes.data);
      setTenantAnalytics(tenantsRes.data);
    } catch (error) {
      toast.error('Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (type) => {
    setExporting(type);
    const token = localStorage.getItem('superAdminToken');
    
    try {
      const endpoint = type === 'tenants' 
        ? `${API}/analytics/platform/export/tenants?format=csv`
        : `${API}/analytics/platform/export/revenue?format=csv`;
      
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`${type} data exported successfully`);
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500"></div>
      </div>
    );
  }

  const planData = overview?.plan_distribution 
    ? Object.entries(overview.plan_distribution).map(([name, value], index) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value,
        color: COLORS[index % COLORS.length]
      }))
    : [];

  const healthData = tenantAnalytics?.health_distribution
    ? Object.entries(tenantAnalytics.health_distribution).map(([name, value]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value
      }))
    : [];

  return (
    <div className="space-y-6" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Reports & Analytics
          </h1>
          <p className="text-slate-400 mt-1">Advanced platform analytics and data exports</p>
        </div>
        <div className="flex gap-3">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-36 bg-slate-800 border-slate-600 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-600 z-[100]">
              <SelectItem value="7d" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Last 7 Days</SelectItem>
              <SelectItem value="30d" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Last 30 Days</SelectItem>
              <SelectItem value="90d" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Last 90 Days</SelectItem>
              <SelectItem value="1y" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Last Year</SelectItem>
              <SelectItem value="all" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">All Time</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Key Metrics */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <MetricBox title="Total Tenants" value={overview.summary.total_tenants} icon={Building2} />
          <MetricBox title="Active Tenants" value={overview.summary.active_tenants} icon={Activity} />
          <MetricBox title="Total Users" value={overview.summary.total_users} icon={Users} />
          <MetricBox title="MRR" value={`$${overview.summary.mrr}`} icon={DollarSign} />
          <MetricBox title="ARR" value={`$${overview.summary.arr}`} icon={TrendingUp} />
          <MetricBox title="Churn Rate" value={`${overview.summary.churn_rate}%`} icon={Activity} />
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Growth Trend */}
        {overview?.growth_trend && overview.growth_trend.length > 0 && (
          <Card className="bg-slate-900 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-violet-400" />
                Signup Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={overview.growth_trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#64748b"
                      tickFormatter={(val) => new Date(val).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
                    />
                    <YAxis stroke="#64748b" />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                      labelStyle={{ color: '#fff' }}
                    />
                    <Bar dataKey="signups" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Plan Distribution */}
        <Card className="bg-slate-900 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-violet-400" />
              Plan Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center gap-8">
              <div className="w-48 h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={planData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {planData.map((entry, index) => (
                        <Cell key={`cell-${entry.name || index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2">
                {planData.map((plan) => (
                  <div key={plan.name} className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: plan.color }} />
                    <span className="text-slate-300 text-sm">{plan.name}</span>
                    <Badge variant="outline" className="text-slate-400 border-slate-600">
                      {plan.value}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tenant Health */}
      {tenantAnalytics && (
        <Card className="bg-slate-900 border-slate-700">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-white">Tenant Engagement Health</CardTitle>
              <CardDescription>Based on activity and usage metrics</CardDescription>
            </div>
            <div className="flex gap-2">
              {healthData.map((item) => (
                <Badge 
                  key={item.name}
                  className={`${
                    item.name === 'High' ? 'bg-emerald-500/20 text-emerald-400' :
                    item.name === 'Medium' ? 'bg-amber-500/20 text-amber-400' :
                    'bg-red-500/20 text-red-400'
                  }`}
                >
                  {item.name}: {item.value}
                </Badge>
              ))}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
              {tenantAnalytics.tenants?.slice(0, 12).map((tenant) => (
                <div 
                  key={tenant.id}
                  className="p-4 bg-slate-800/50 rounded-lg border border-slate-700"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-white font-medium text-sm">{tenant.name}</p>
                      <p className="text-slate-500 text-xs">{tenant.slug}</p>
                    </div>
                    <Badge 
                      className={`${
                        tenant.health === 'high' ? 'bg-emerald-500/20 text-emerald-400' :
                        tenant.health === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                        'bg-red-500/20 text-red-400'
                      }`}
                    >
                      {tenant.engagement_score} pts
                    </Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className="text-center">
                      <p className="text-white font-bold">{tenant.metrics.users}</p>
                      <p className="text-slate-500">Users</p>
                    </div>
                    <div className="text-center">
                      <p className="text-white font-bold">{tenant.metrics.positions}</p>
                      <p className="text-slate-500">Positions</p>
                    </div>
                    <div className="text-center">
                      <p className="text-white font-bold">{tenant.metrics.joinings}</p>
                      <p className="text-slate-500">Joinings</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Export Section */}
      <Card className="bg-slate-900 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <FileDown className="h-5 w-5 text-violet-400" />
            Data Exports
          </CardTitle>
          <CardDescription>Download detailed reports in CSV format</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <ExportCard
              title="Tenant Report"
              description="All tenants with details and metrics"
              icon={Building2}
              onClick={() => handleExport('tenants')}
              loading={exporting === 'tenants'}
            />
            <ExportCard
              title="Revenue Report"
              description="All payment transactions"
              icon={DollarSign}
              onClick={() => handleExport('revenue')}
              loading={exporting === 'revenue'}
            />
            <ExportCard
              title="Full Analytics"
              description="Comprehensive platform data"
              icon={BarChart3}
              onClick={() => toast.info('Coming soon')}
              disabled
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function MetricBox({ title, value, icon: Icon }) {
  return (
    <Card className="bg-slate-800/50 border-slate-700">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-violet-600/20 rounded">
            <Icon className="h-4 w-4 text-violet-400" />
          </div>
          <div>
            <p className="text-lg font-bold text-white">{value}</p>
            <p className="text-xs text-slate-400">{title}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ExportCard({ title, description, icon: Icon, onClick, loading, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      className={`p-4 bg-slate-800/50 border border-slate-700 rounded-lg text-left transition-colors ${
        disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-violet-500 hover:bg-slate-800'
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="p-2 bg-violet-600/20 rounded">
          <Icon className="h-5 w-5 text-violet-400" />
        </div>
        {loading && (
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-500"></div>
        )}
      </div>
      <p className="text-white font-medium mt-3">{title}</p>
      <p className="text-slate-500 text-sm mt-1">{description}</p>
      <div className="flex items-center gap-1 text-violet-400 text-sm mt-3">
        <Download className="h-4 w-4" />
        Download CSV
      </div>
    </button>
  );
}
